// Территории: каталоги по источникам, уровни, фильтры по региону и году.

import { LEVEL_LABELS as LOCALIZED_LEVEL_LABELS } from '../i18n';
import type { Territory, TerritoryLevel } from '../types';
import { api } from '../api';

import { controls } from './controls';
import { currentCollator, currentLocale } from './locale';
import { currentIndicator, isBdmoIndicator, isEmissIndicator, isSettlementIndicator, isWorldBankIndicator, updateAdvisor } from './indicators';
import { errorMessage, showFormError } from './status';
import { escapeHtml } from './dom';
import { selectedValues, selectedYearsFromForm, setSelectedValues, syncChoiceMirror } from './choice-lists';
import { state } from './state';
import { updateSubmitState } from './widget-form';

export function mergeKnownTerritories(items: Territory[]): void {
  const merged = new Map(state.territories.map((item) => [item.id, item]));
  items.forEach((item) => merged.set(item.id, item));
  state.territories = [...merged.values()];
}

export function setCatalogTerritories(items: Territory[]): void {
  state.catalogTerritories = items;
  mergeKnownTerritories(items);
}

export function configureTerritoryLevelOptions(preferred?: TerritoryLevel): void {
  const bdmo = isBdmoIndicator();
  const settlements = isSettlementIndicator();
  const worldbank = isWorldBankIndicator();
  const current = preferred ?? (controls.territoryLevel.value as TerritoryLevel);
  const levelLabels = LOCALIZED_LEVEL_LABELS[currentLocale()];
  const options: Array<[TerritoryLevel, string]> = bdmo
    ? [
        ['municipality_upper', levelLabels.municipality_upper],
        ['municipality_lower', levelLabels.municipality_lower],
      ]
    : settlements
      ? [
          ['settlement', levelLabels.settlement],
        ]
      : worldbank
        ? [
            ['country', levelLabels.country],
          ]
    : [
        ['country', levelLabels.country],
        ['federal_district', levelLabels.federal_district],
        ['region', levelLabels.region],
        ['municipality', levelLabels.municipality],
      ];
  // ЕМИСС: только уровни, где у показателя есть территории (каталог бэкенда
  // сверен с метаданными источника). Каталог ещё не загружен — все уровни.
  const availableLevels = isEmissIndicator()
    ? new Set(state.catalogTerritories.filter((item) => item.selectable !== false).map((item) => item.level))
    : null;
  if (availableLevels?.size) {
    const narrowed = options.filter(([value]) => availableLevels.has(value));
    if (narrowed.length) options.splice(0, options.length, ...narrowed);
  }
  controls.territoryLevel.innerHTML = options
    .map(([value, label]) => `<option value="${value}">${escapeHtml(label)}</option>`)
    .join('');
  const allowed = options.map(([value]) => value);
  controls.territoryLevel.value = allowed.includes(current) ? current : options[0]?.[0] ?? 'region';
}

export function populateTerritoryFilters(): void {
  const districts = state.catalogTerritories
    .filter((territory) => territory.level === 'federal_district')
    .sort((a, b) => currentCollator().compare(a.name, b.name));
  const previous = controls.districtFilter.value;
  controls.districtFilter.innerHTML = [
    '<option value="">Все округа</option>',
    ...districts.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.name)}</option>`),
  ].join('');
  if (districts.some((item) => item.id === previous)) controls.districtFilter.value = previous;
  rebuildRegionFilter();
}

export function rebuildRegionFilter(preserve = true): void {
  const previous = preserve ? controls.regionFilter.value : '';
  const district = controls.districtFilter.value;
  const regions = state.catalogTerritories
    .filter(
      (territory) =>
        territory.level === 'region' && (!district || territory.federal_district_id === district),
    )
    .sort((a, b) => currentCollator().compare(a.name, b.name));
  controls.regionFilter.innerHTML = [
    `<option value="">${isBdmoIndicator() ? 'Выберите субъект РФ' : 'Все субъекты'}</option>`,
    ...regions.map((item) => `<option value="${escapeHtml(item.id)}">${escapeHtml(item.name)}</option>`),
  ].join('');
  if (regions.some((item) => item.id === previous)) controls.regionFilter.value = previous;
}

export function filteredTerritories(): Territory[] {
  const level = controls.territoryLevel.value as TerritoryLevel;
  const district = level === 'federal_district' || level === 'country' ? '' : controls.districtFilter.value;
  const region = controls.regionFilter.value;
  return state.catalogTerritories
    .filter((territory) => territory.level === level && territory.selectable !== false)
    .filter((territory) => !district || territory.federal_district_id === district)
    .filter((territory) => !['municipality', 'municipality_upper', 'municipality_lower', 'settlement'].includes(level) || !region || territory.region_id === region)
    .sort((a, b) => currentCollator().compare(a.name, b.name));
}

export function updateTerritoryCount(): void {
  const selected = controls.territories.selectedOptions.length;
  const visible = controls.territories.options.length;
  controls.territoryCount.textContent = `Выбрано: ${selected} из ${visible}`;
  updateAdvisor();
  updateSubmitState();
}

export function rebuildTerritoryOptions(preferred?: string[], selectDefault = false): void {
  const previous = preferred ?? selectedValues(controls.territories);
  const available = filteredTerritories();
  controls.territories.innerHTML = available
    .map((territory) => {
      const code = territory.oktmo ? ` · ОКТМО ${territory.oktmo}` : '';
      return `<option value="${escapeHtml(territory.id)}" title="${escapeHtml(territory.name + code)}">${escapeHtml(territory.name)}</option>`;
    })
    .join('');
  const availableIds = new Set(available.map((territory) => territory.id));
  const preserved = previous.filter((id) => availableIds.has(id));
  if (preserved.length) {
    setSelectedValues(controls.territories, preserved);
  } else if (selectDefault && !isSettlementIndicator()) {
    const defaultCount = isBdmoIndicator()
      ? Math.min(5, available.length)
      : isWorldBankIndicator()
        ? Math.min(10, available.length)
        : available.length;
    Array.from(controls.territories.options).forEach((option, index) => {
      option.selected = index < defaultCount;
    });
  }
  syncChoiceMirror(controls.territories);
  updateTerritoryCount();
}

export function selectedCatalogYear(): number | undefined {
  const years = selectedYearsFromForm();
  return years.length ? Math.max(...years) : undefined;
}

export function preferredBdmoLevel(ids: string[]): TerritoryLevel | undefined {
  const first = ids[0] ?? '';
  if (first.startsWith('bdmo:municipality_lower:')) return 'municipality_lower';
  if (first.startsWith('bdmo:municipality_upper:')) return 'municipality_upper';
  return undefined;
}

export async function loadBdmoMunicipalities(
  preferred: string[] = [],
  selectDefault = false,
  token = ++state.catalogRequestToken,
): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator || !isBdmoIndicator(indicator)) return;
  const level = controls.territoryLevel.value as TerritoryLevel;
  if (level !== 'municipality_upper' && level !== 'municipality_lower') return;
  const regionId = controls.regionFilter.value;
  const base = state.catalogTerritories.filter((item) => item.level === 'federal_district' || item.level === 'region');
  if (!regionId) {
    setCatalogTerritories(base);
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    return;
  }
  controls.territories.innerHTML = '<option disabled>Загрузка муниципалитетов…</option>';
  syncChoiceMirror(controls.territories);
  try {
    const municipalities = await api.territories({
      sourceType: 'bdmo',
      indicatorId: indicator.id,
      level,
      regionId,
      year: selectedCatalogYear(),
      limit: 20_000,
    }, currentLocale());
    if (token !== state.catalogRequestToken) return;
    setCatalogTerritories([...base, ...municipalities]);
    rebuildTerritoryOptions(preferred, selectDefault);
  } catch (error) {
    if (token !== state.catalogRequestToken) return;
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    showFormError(`Не удалось получить муниципалитеты БДМО: ${errorMessage(error)}`);
  }
}

export async function loadBdmoCatalog(preferred: string[] = []): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator || !isBdmoIndicator(indicator)) return;
  const token = ++state.catalogRequestToken;
  configureTerritoryLevelOptions(preferredBdmoLevel(preferred));
  const level = controls.territoryLevel.value as TerritoryLevel;
  const year = selectedCatalogYear();
  controls.territories.innerHTML = '<option disabled>Загрузка территориального каталога…</option>';
  syncChoiceMirror(controls.territories);
  try {
    const resolvePromise = preferred[0]
      ? api.territories({
          sourceType: 'bdmo',
          indicatorId: indicator.id,
          level,
          territoryId: preferred[0],
          year,
          limit: 1,
        }, currentLocale())
      : Promise.resolve([] as Territory[]);
    const [districts, regions, resolved] = await Promise.all([
      api.territories({ sourceType: 'bdmo', indicatorId: indicator.id, level: 'federal_district', year, limit: 20 }, currentLocale()),
      api.territories({ sourceType: 'bdmo', indicatorId: indicator.id, level: 'region', year, limit: 500 }, currentLocale()),
      resolvePromise,
    ]);
    if (token !== state.catalogRequestToken) return;
    setCatalogTerritories([...districts, ...regions, ...resolved]);
    populateTerritoryFilters();
    const resolvedTerritory = resolved[0];
    if (resolvedTerritory?.federal_district_id) controls.districtFilter.value = resolvedTerritory.federal_district_id;
    rebuildRegionFilter(false);
    if (resolvedTerritory?.region_id) {
      controls.regionFilter.value = resolvedTerritory.region_id;
    } else {
      const firstRegion = Array.from(controls.regionFilter.options).find((option) => option.value);
      controls.regionFilter.value = firstRegion?.value ?? '';
    }
    await loadBdmoMunicipalities(preferred, true, token);
  } catch (error) {
    if (token !== state.catalogRequestToken) return;
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    showFormError(`Не удалось загрузить территориальный каталог БДМО: ${errorMessage(error)}`);
  }
}

export async function loadEmissTerritoryCatalog(preferred: string[] = []): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator || !isEmissIndicator(indicator)) return;
  const token = ++state.catalogRequestToken;
  const preferredLevel = state.territories.find((territory) => territory.id === preferred[0])?.level;
  // Каталог прежнего показателя не должен сужать уровни этого.
  state.catalogTerritories = [];
  configureTerritoryLevelOptions(preferredLevel);
  controls.territories.innerHTML = '<option disabled>Загрузка территорий ЕМИСС…</option>';
  syncChoiceMirror(controls.territories);
  try {
    const territories = await api.territories({
      sourceType: 'emiss',
      indicatorId: indicator.id,
      limit: 20_000,
    }, currentLocale());
    if (token !== state.catalogRequestToken) return;
    setCatalogTerritories(territories);
    configureTerritoryLevelOptions(preferredLevel);
    populateTerritoryFilters();
    await updateTerritoryControls(Boolean(!preferred.length), preferred);
  } catch (error) {
    if (token !== state.catalogRequestToken) return;
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    showFormError(`Не удалось загрузить территориальный каталог ЕМИСС: ${errorMessage(error)}`);
  }
}

export async function loadWorldBankTerritoryCatalog(preferred: string[] = []): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator || !isWorldBankIndicator(indicator)) return;
  const token = ++state.catalogRequestToken;
  configureTerritoryLevelOptions('country');
  controls.territories.innerHTML = '<option disabled>Загрузка стран международной статистики…</option>';
  syncChoiceMirror(controls.territories);
  try {
    const territories = await api.territories({
      sourceType: 'worldbank',
      indicatorId: indicator.id,
      level: 'country',
      limit: 500,
    }, currentLocale());
    if (token !== state.catalogRequestToken) return;
    setCatalogTerritories(territories);
    populateTerritoryFilters();
    await updateTerritoryControls(Boolean(!preferred.length), preferred);
  } catch (error) {
    if (token !== state.catalogRequestToken) return;
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    showFormError(`Не удалось загрузить страны международной статистики: ${errorMessage(error)}`);
  }
}

// Округа и субъекты для фильтров НП — справочник ГИС (ru_federal_district:*, ru_region:*):
// бэкенд привязывает НП к субъекту по названию региона и фильтрует по нему.
let settlementRegionCatalog: Promise<Territory[]> | null = null;

function loadSettlementRegionCatalog(): Promise<Territory[]> {
  settlementRegionCatalog ??= Promise.all(
    (['federal_district', 'region'] as const).map((level) => api.territories({ sourceType: 'emiss', level, limit: 500 }, currentLocale())),
  )
    .then((groups) => groups.flat().filter((item) => item.id.startsWith('ru_federal_district:') || item.id.startsWith('ru_region:')))
    .catch((error) => {
      settlementRegionCatalog = null;
      throw error;
    });
  return settlementRegionCatalog;
}

export async function loadSettlementCatalog(
  preferred: string[] = [],
  selectDefault = false,
  token = ++state.catalogRequestToken,
): Promise<void> {
  const indicator = currentIndicator();
  if (!indicator || !isSettlementIndicator(indicator)) return;
  configureTerritoryLevelOptions('settlement');
  controls.territorySearchField.hidden = false;
  controls.districtFilterField.hidden = false;
  controls.regionFilterField.hidden = false;
  controls.territories.innerHTML = '<option disabled>Загрузка населённых пунктов…</option>';
  syncChoiceMirror(controls.territories);
  try {
    // Каталог ещё не каталог НП (другой показатель) — фильтры строятся заново.
    const firstLoad = !state.catalogTerritories.some((item) => item.level === 'settlement')
      || !state.catalogTerritories.some((item) => item.id.startsWith('ru_region:'));
    const [regionCatalog, resolved] = await Promise.all([
      loadSettlementRegionCatalog(),
      preferred.length
        ? Promise.all(preferred.map((territoryId) => api.territories({
            sourceType: 'settlements',
            indicatorId: indicator.id,
            level: 'settlement',
            territoryId,
            limit: 1,
          }, currentLocale()))).then((groups) => groups.flat())
        : Promise.resolve([] as Territory[]),
    ]);
    if (token !== state.catalogRequestToken) return;
    if (firstLoad || preferred.length) {
      setCatalogTerritories(regionCatalog);
      populateTerritoryFilters();
      // Сохранённый виджет — фильтры по субъекту его первого НП.
      const first = resolved[0];
      if (first) {
        controls.districtFilter.value = first.federal_district_id ?? '';
        rebuildRegionFilter(false);
        controls.regionFilter.value = first.region_id ?? '';
      }
    }
    const region = controls.regionFilter.value;
    const district = controls.districtFilter.value;
    const search = controls.territorySearch.value.trim();
    const results = await api.territories({
      sourceType: 'settlements',
      indicatorId: indicator.id,
      level: 'settlement',
      search,
      regionId: region || undefined,
      federalDistrictId: region ? undefined : district || undefined,
      // По субъекту — весь его список (самый длинный — ~10 тыс.), иначе первые 200.
      limit: region ? 20_000 : 200,
    }, currentLocale());
    if (token !== state.catalogRequestToken) return;
    const merged = new Map<string, Territory>();
    [...resolved, ...results].forEach((item) => merged.set(item.id, item));
    setCatalogTerritories([...regionCatalog, ...merged.values()]);
    rebuildTerritoryOptions(preferred, selectDefault);
  } catch (error) {
    if (token !== state.catalogRequestToken) return;
    controls.territories.innerHTML = '';
    syncChoiceMirror(controls.territories);
    updateTerritoryCount();
    showFormError(`Не удалось загрузить населённые пункты: ${errorMessage(error)}`);
  }
}

export function activateLocalTerritoryCatalog(preferred: string[] = []): void {
  state.catalogRequestToken += 1;
  configureTerritoryLevelOptions(
    state.localTerritories.find((territory) => territory.id === preferred[0])?.level,
  );
  setCatalogTerritories(state.localTerritories);
  populateTerritoryFilters();
  const firstTerritory = state.localTerritories.find((territory) => territory.id === preferred[0]);
  if (firstTerritory) {
    controls.districtFilter.value = firstTerritory.federal_district_id ?? '';
    rebuildRegionFilter(false);
    controls.regionFilter.value = firstTerritory.region_id ?? '';
  }
  updateTerritoryControls(Boolean(!preferred.length), preferred);
}

export async function updateTerritoryControls(selectDefault = false, preferred: string[] = []): Promise<void> {
  const level = controls.territoryLevel.value as TerritoryLevel;
  const bdmo = isBdmoIndicator();
  const settlements = isSettlementIndicator();
  controls.territorySearchField.hidden = !settlements;
  controls.districtFilterField.hidden = level === 'federal_district' || level === 'country';
  controls.regionFilterField.hidden = !settlements && (bdmo
    ? !(level === 'municipality_upper' || level === 'municipality_lower')
    : level !== 'municipality');
  rebuildRegionFilter();
  if (bdmo) {
    await loadBdmoMunicipalities(preferred, selectDefault);
  } else if (settlements) {
    await loadSettlementCatalog(preferred, selectDefault);
  } else {
    rebuildTerritoryOptions(preferred, selectDefault);
  }
}
